// CTV-516: shared payment-ledger model for the Order Receipt (rendered both
// in the React component and the downloadable PDF). The receipt is the single
// source of truth for a campaign's money, so it must reflect:
//   - monthly: the live per-installment schedule (post-extension), with each
//     row's actual paid date (or scheduled invoice date) and status;
//   - upfront: the initial payment plus each extension charge as its own row,
//     with actual charge dates / statuses, summing to a final total of the
//     original authorized amount + every extension that actually charged.
//
// Failed / pending / refunded rows still render (with their status) but do not
// count toward the paid total.

import {
  buildChargeSchedule,
  type SelfServiceInstallment,
  type SelfServiceSubmissionRow,
} from '../../types/selfService';

export type ReceiptPaymentStatus =
  | 'paid'
  | 'authorized'
  | 'scheduled'
  | 'processing'
  | 'pending'
  | 'failed'
  | 'refunded'
  | 'released'
  | 'voided'
  | 'canceled';

// A single charge in the upfront ledger (initial + one per extension).
export interface ReceiptUpfrontRow {
  key: string;
  label: string; // "Initial payment" | "Extension"
  amountCents: number;
  // Successful charge/capture date when available, else null.
  date: string | null;
  status: ReceiptPaymentStatus;
  // Only paid rows roll into the final total.
  countsTowardTotal: boolean;
}

// A single month in the monthly schedule. Mirrors ChargeScheduleEntry plus the
// status + effective date the receipt now shows per row.
export interface ReceiptScheduleRow {
  key: string;
  label: string;
  coversLabel: string;
  amountCents: number;
  isPartial: boolean;
  status: ReceiptPaymentStatus;
  // Paid installments → paidAt; not-yet-charged → the scheduled invoice
  // (due) date; legacy fallback rows have no explicit date (the label is the
  // period start).
  date: string | null;
}

// Extension charge fed in from the page's edit log. Monthly extensions bump
// installments (already reflected in the schedule) so they are NOT passed
// here — only upfront extensions, which charge as discrete payments.
export interface ReceiptExtensionCharge {
  id: string;
  amountCents: number;
  date: string | null;
  status: ReceiptPaymentStatus;
}

// Status + display copy for the upfront initial payment, derived from the
// submission status. The capture date for an approved (charged) upfront
// campaign is its review timestamp — approval is what triggers capture.
export function upfrontInitialPayment(
  row: Pick<SelfServiceSubmissionRow, 'status' | 'reviewed_at' | 'amount_cents'>,
): ReceiptUpfrontRow {
  const base = { key: 'initial', label: 'Initial payment', amountCents: row.amount_cents ?? 0 };
  switch (row.status) {
    case 'approved':
      return { ...base, date: row.reviewed_at, status: 'paid', countsTowardTotal: true };
    case 'payment_authorized':
      return { ...base, date: null, status: 'authorized', countsTowardTotal: false };
    case 'payment_failed':
      return { ...base, date: null, status: 'failed', countsTowardTotal: false };
    case 'payment_refunded':
      return { ...base, date: row.reviewed_at, status: 'refunded', countsTowardTotal: false };
    case 'creative_rejected':
    case 'archived':
    case 'canceled':
      return { ...base, date: null, status: 'released', countsTowardTotal: false };
    default:
      return { ...base, date: null, status: 'pending', countsTowardTotal: false };
  }
}

// Build the upfront ledger: initial payment + every extension charge. The
// total sums only rows that actually charged (the original authorized total
// plus extensions that captured).
export function buildUpfrontPayments(
  row: Pick<SelfServiceSubmissionRow, 'status' | 'reviewed_at' | 'amount_cents'>,
  extensions: ReceiptExtensionCharge[] = [],
): { rows: ReceiptUpfrontRow[]; totalCents: number } {
  const initial = upfrontInitialPayment(row);
  const extRows: ReceiptUpfrontRow[] = extensions.map((e) => ({
    key: e.id,
    label: 'Extension',
    amountCents: e.amountCents,
    date: e.date,
    status: e.status,
    countsTowardTotal: e.status === 'paid',
  }));
  const rows = [initial, ...extRows];
  const totalCents = rows.reduce((s, r) => s + (r.countsTowardTotal ? r.amountCents : 0), 0);
  return { rows, totalCents };
}

function installmentStatus(s: SelfServiceInstallment['status']): ReceiptPaymentStatus {
  // CTV-576: a not-yet-approved installment is 'pending_approval' in the DB;
  // the receipt shows it as the generic 'Pending'. Everything else (scheduled,
  // processing, paid, failed, canceled) is already a valid receipt status and
  // passes through 1:1.
  if (s === 'pending_approval') return 'pending';
  return s;
}

// Build the monthly schedule rows. Prefers the live installments (so
// post-extension bumps, inserted flights, and actual paid dates show); falls
// back to the at-submission projection when installments haven't been
// materialized yet (e.g. before approval).
export function buildMonthlyReceiptSchedule(
  row: Pick<
    SelfServiceSubmissionRow,
    'installments' | 'campaign' | 'amount_cents'
  >,
): ReceiptScheduleRow[] {
  const installments = row.installments ?? [];
  if (installments.length > 0) {
    const sorted = [...installments].sort((a, b) => a.installmentNumber - b.installmentNumber);
    return sorted.map((inst) => {
      const entry = installmentToScheduleRow(inst);
      return entry;
    });
  }
  // Fallback: no live schedule yet. Project equal anniversary charges from the
  // campaign window; these are all unbilled, so mark them scheduled.
  // amount_cents is the stored buyer total (budget + any grandfathered
  // surcharge), so splitting it keeps the projection faithful for both fee
  // models.
  const total = (row.amount_cents ?? 0) / 100;
  return buildChargeSchedule(row.campaign?.startDate, row.campaign?.endDate, total).map(
    (e, i) => ({
      key: `proj-${i}`,
      label: e.label,
      coversLabel: e.coversLabel,
      amountCents: Math.round(e.amount * 100),
      isPartial: e.isPartial,
      status: 'scheduled' as const,
      date: null,
    }),
  );
}

function installmentToScheduleRow(inst: SelfServiceInstallment): ReceiptScheduleRow {
  const fmtLong = (s: string) => {
    const [y, m, d] = s.slice(0, 10).split('-').map(Number);
    const date = new Date(Date.UTC(y, (m ?? 1) - 1, d ?? 1));
    return isNaN(date.getTime())
      ? s
      : date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  };
  const fmtShort = (s: string) => {
    const [y, m, d] = s.slice(0, 10).split('-').map(Number);
    const date = new Date(Date.UTC(y, (m ?? 1) - 1, d ?? 1));
    return isNaN(date.getTime())
      ? s
      : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  };
  const startMs = Date.parse(inst.periodStart);
  const endMs = Date.parse(inst.periodEnd);
  const days = isNaN(startMs) || isNaN(endMs) ? 0 : Math.floor((endMs - startMs) / 86400000) + 1;
  // A partial month is shorter than ~28 days (the shortest natural month);
  // good enough to flag prorated tails without re-deriving anniversary math.
  const isPartial = days > 0 && days < 28;
  const status = installmentStatus(inst.status);
  return {
    key: inst.id,
    label: fmtLong(inst.periodStart),
    coversLabel: `${fmtShort(inst.periodStart)} – ${fmtShort(inst.periodEnd)}`,
    amountCents: inst.amountCents,
    isPartial,
    status,
    // Paid → actual capture date; otherwise the scheduled invoice (due) date.
    date: inst.status === 'paid' ? inst.paidAt : inst.dueDate,
  };
}

// Human label for a payment status, shown as a small badge in the receipt.
export function paymentStatusLabel(status: ReceiptPaymentStatus): string {
  switch (status) {
    case 'paid':
      return 'Paid';
    case 'authorized':
      return 'Authorized';
    case 'scheduled':
      return 'Scheduled';
    case 'processing':
      return 'Processing';
    case 'pending':
      return 'Pending';
    case 'failed':
      return 'Failed';
    case 'refunded':
      return 'Refunded';
    case 'released':
      return 'Hold released';
    case 'voided':
      return 'Voided';
    case 'canceled':
      return 'Canceled';
    default:
      return status;
  }
}
